import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "./firebase";
import { currentPeriod } from "../features/subscription/planConfig";

const COLLECTION = "users";

// ============================================================
// GET OR CREATE USER ACCOUNT
// ============================================================
// Creates the user's subscription document on first login if it
// doesn't exist yet, defaulting to the Free plan with zero usage.
export async function getOrCreateUserAccount(uid) {
  const ref = doc(db, COLLECTION, uid);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    return snap.data();
  }

  const initial = {
    plan: "free",
    usage: {
      period: currentPeriod(),
      count: 0,
    },
    createdAt: serverTimestamp(),
  };

  await setDoc(ref, initial);

  return initial;
}

// ============================================================
// REAL-TIME ACCOUNT LISTENER
// ============================================================
// Keeps the frontend synchronized with Firestore.
//
// This means when the Paddle webhook changes:
//
// plan: "free" → "premium"
//
// the SubscriptionContext receives the update immediately without
// requiring the user to refresh the page.
export function subscribeToUserAccount(uid, callback) {
  const ref = doc(db, COLLECTION, uid);

  return onSnapshot(
    ref,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data());
      }
    },
    (error) => {
      console.error("Failed to listen to subscription account:", error);
    },
  );
}

// ============================================================
// SET USER PLAN
// ============================================================
// Kept for development/admin usage.
// Real paid subscription changes should come from Paddle webhooks.
export async function setUserPlan(uid, plan) {
  await updateDoc(
    doc(db, COLLECTION, uid),
    { plan },
  );
}

// ============================================================
// GET USAGE
// ============================================================
// Reads current usage and resets it if we've rolled into a new
// month. The reset happens lazily on read/write.
export async function getUsage(uid) {
  const ref = doc(db, COLLECTION, uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return {
      period: currentPeriod(),
      count: 0,
    };
  }

  const data = snap.data();
  const period = currentPeriod();

  if (data.usage?.period !== period) {
    const resetUsage = {
      period,
      count: 0,
    };

    await updateDoc(ref, {
      usage: resetUsage,
    });

    return resetUsage;
  }

  return data.usage;
}

// ============================================================
// INCREMENT USAGE
// ============================================================
// Increments usage by 1 while handling monthly rollover.
export async function incrementUsage(uid) {
  const ref = doc(db, COLLECTION, uid);
  const current = await getUsage(uid);

  const updated = {
    period: current.period,
    count: current.count + 1,
  };

  await updateDoc(ref, {
    usage: updated,
  });

  return updated;
}